import { motion } from "framer-motion";
import Seo from "../components/Seo";
import LeadForm from "../components/LeadForm";
import FAQ from "../components/FAQ";
import ServiceCTA from "../components/service/ServiceCTA";
import { KineticLines, Reveal, SectionLabel } from "../components/Reveal";
import { ArrowRight, Package, CreditCard, Truck, FileSearch, Gauge, SearchCheck } from "lucide-react";

const AREAS = [
  { icon: Package, title: "Operating expense reviews", copy: "A careful look at the recurring costs that quietly accumulate across your operation." },
  { icon: Truck, title: "Shipping & logistics expenses", copy: "Small parcel, freight, and delivery spend reviewed against today's rate landscape." },
  { icon: CreditCard, title: "Commercial payment expenses", copy: "Card acceptance and payment processing costs examined line by line." },
  { icon: FileSearch, title: "Expense recovery opportunities", copy: "Some costs may qualify for recovery or refunds — specialists check what could apply." },
  { icon: Gauge, title: "Operational efficiency", copy: "Where processes and vendors may be working harder than they should for your budget." },
  { icon: SearchCheck, title: "Identifying overlooked costs", copy: "The charges that hide in plain sight — found, flagged, and explained." },
];

const FAQS = [
  { q: "What happens during a business expense review?", a: "A specialist looks at the areas you approve — such as shipping, payment processing, and other operating costs — and identifies where expenses may be reduced or recovered. Findings are explained clearly before anything changes." },
  { q: "Do I have to switch vendors or providers?", a: "No. Recommendations are options, not requirements. Many owners simply use the findings to confirm their current setup is competitive." },
  { q: "What are expense recovery opportunities?", a: "Some businesses are entitled to refunds or credits they never claimed — for example, from billing errors or unclaimed programs. Specialists can check whether any apply to your situation." },
  { q: "Is there a fee?", a: "Starting a review costs nothing. If a specialist engagement makes sense, any costs are explained up front — before you decide anything." },
];

function ConvergenceViz() {
  const rows = ["Shipping", "Payment processing", "Utilities & services", "Recovery checks"];
  return (
    <div className="relative rounded-3xl bg-white border border-line shadow-core p-6 md:p-8" data-testid="expense-viz">
      <svg viewBox="0 0 460 260" className="w-full h-auto" role="img" aria-label="Expense categories converging into one simplified business expense overview">
        <defs>
          <linearGradient id="pgExp" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#B9CFE4" />
            <stop offset="100%" stopColor="#15B8A6" />
          </linearGradient>
        </defs>
        {rows.map((r, i) => (
          <g key={r} className="group/row cursor-default">
            <circle cx="30" cy={40 + i * 60} r="7" fill="#234D85" className="transition-colors duration-300 group-hover/row:fill-[#15B8A6]" />
            <text x="50" y={44 + i * 60} fontSize="12.5" fontWeight="700" fill="#101F38" style={{ fontFamily: "Inter, sans-serif" }}>{r}</text>
            <path d={`M44 ${40 + i * 60} C 160 ${40 + i * 60}, 250 130, 330 130`} fill="none" stroke="url(#pgExp)" strokeWidth="2" strokeDasharray="5 7" className="animate-dashFlow opacity-80" />
          </g>
        ))}
        <circle cx="352" cy="130" r="40" fill="#101F38" />
        <text x="352" y="126" textAnchor="middle" fontSize="10" fontWeight="800" fill="#FFFFFF" style={{ fontFamily: "Inter, sans-serif" }}>SIMPLIFIED</text>
        <text x="352" y="141" textAnchor="middle" fontSize="10" fontWeight="800" fill="#2BD4C1" style={{ fontFamily: "Inter, sans-serif" }}>OVERVIEW</text>
        <path d="M394 130 H 424" stroke="#15B8A6" strokeWidth="2.4" strokeDasharray="4 6" className="animate-dashFlow" />
      </svg>
      <motion.p
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mt-2 text-center text-xs font-semibold text-slate"
      >
        Hover a category — every line flows into one clear picture.
      </motion.p>
    </div>
  );
}

export default function ExpenseSavings() {
  return (
    <main data-testid="page-expense-savings">
      <Seo
        title="Business Expense Savings"
        description="Operating expense reviews, shipping and payment processing costs, and potential expense recovery — connected with independent specialists who evaluate what may be possible."
        path="/expense-savings"
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-ice pt-28 lg:pt-36 pb-16 md:pb-24">
        <div className="absolute inset-0 bg-arch" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionLabel>Business Expense Savings</SectionLabel>
            <KineticLines
              as="h1"
              className="mt-5 font-display font-extrabold tracking-tightest text-navy text-4xl sm:text-5xl leading-[1.06]"
              lines={["Find Opportunities", <>Hidden in <span className="text-teal-dark">Everyday Expenses.</span></>]}
            />
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-slate">
                Many businesses have operating expenses that haven't been carefully reviewed in years. We help
                connect you with specialists who evaluate areas such as shipping costs, payment processing, and other
                potential expense-reduction or recovery opportunities.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <a href="#expense-review" data-testid="expense-hero-cta" className="group mt-8 inline-flex items-center gap-2 bg-navy text-white font-bold text-[15px] px-7 py-4 rounded-full transition-all duration-300 hover:bg-teal hover:text-navy shadow-lift">
                Request a Business Expense Review
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </Reveal>
          </div>
          <Reveal delay={0.2}><ConvergenceViz /></Reveal>
        </div>
      </section>

      {/* Areas */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <SectionLabel>What A Review Can Look At</SectionLabel>
            <KineticLines as="h2" className="mt-5 font-display font-extrabold tracking-tightest text-navy text-3xl sm:text-4xl leading-[1.1]" lines={["Where costs hide — and where help can look."]} />
          </div>
          <div className="mt-10 border-t border-line">
            {AREAS.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.04}>
                <div className="group grid grid-cols-[auto_1fr] md:grid-cols-[64px_1fr_1.2fr] items-start gap-4 md:gap-8 py-7 border-b border-line transition-colors hover:bg-ice/50" data-testid={`expense-area-${i + 1}`}>
                  <span className="font-display font-extrabold text-slate-light text-lg pt-0.5">0{i + 1}</span>
                  <h3 className="flex items-center gap-3 font-display font-extrabold tracking-tight text-navy text-lg md:text-xl">
                    <a.icon className="w-5 h-5 text-teal-dark shrink-0" />
                    {a.title}
                  </h3>
                  <p className="col-span-2 md:col-span-1 text-[15px] leading-relaxed text-slate">{a.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="mt-6 text-xs text-slate-light">
              Evaluations are performed by independent specialists. Availability of specific review types can vary.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Who + process */}
      <section className="py-16 md:py-24 bg-ice/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <SectionLabel>Who This Helps</SectionLabel>
            <KineticLines as="h2" className="mt-5 font-display font-extrabold tracking-tightest text-navy text-3xl leading-[1.1]" lines={["Built for owners who watch every dollar."]} />
            <Reveal delay={0.1}>
              <p className="mt-5 text-base leading-relaxed text-slate">
                Retailers and restaurants with shipping and payment volume. Distributors with freight-heavy
                operations. Professional firms with lean overhead. Local businesses that suspect they're overpaying
                somewhere but can't prove where. If expenses are part of your operation, a review can be part of your
                answer.
              </p>
            </Reveal>
          </div>
          <div>
            <SectionLabel>How The Process Works</SectionLabel>
            <div className="mt-6 space-y-6">
              {[
                ["You share the basics", "Tell us which expense areas you'd like reviewed — nothing more."],
                ["A specialist digs in", "An independent specialist evaluates your costs and identifies potential reductions or recoveries."],
                ["You see the findings", "Clear explanations, clear numbers, zero pressure to act."],
              ].map(([t, c], i) => (
                <Reveal key={t} delay={i * 0.08}>
                  <div className="flex gap-4">
                    <span className="flex items-center justify-center w-9 h-9 rounded-full bg-navy text-teal-bright text-xs font-extrabold shrink-0">{i + 1}</span>
                    <div>
                      <h3 className="font-display font-extrabold text-navy">{t}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-slate">{c}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FAQ items={FAQS} title="Expense Review Questions" label="FAQ" />

      {/* Lead form */}
      <section id="expense-review" className="py-16 md:py-24 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionLabel>Request A Review</SectionLabel>
            <KineticLines as="h2" className="mt-5 font-display font-extrabold tracking-tightest text-navy text-3xl sm:text-4xl leading-[1.1]" lines={["See what a fresh look at your expenses finds."]} />
            <Reveal delay={0.1}>
              <p className="mt-5 text-base leading-relaxed text-slate max-w-lg">
                Tell us a little about your business and we'll help you connect with a specialist who evaluates
                operating expenses. No cost to start, no obligation to continue.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1}><LeadForm service="expense" heading="Request a Business Expense Review" /></Reveal>
        </div>
      </section>

      <ServiceCTA
        service="expense"
        title={["Your expenses, re-examined —", "with specialists who do this every day."]}
        copy="Start with a quick conversation. We'll point the right specialists at the areas that matter most."
        cta="Request a Business Expense Review"
      />
    </main>
  );
}
